import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const JobHeader = ({ job, onApply, onSaveToWishlist, isSaved }) => {
  const navigate = useNavigate();
  const [showShareMenu, setShowShareMenu] = useState(false);

  const handleShare = (platform) => {
    const shareUrl = window.location?.href;
    const shareText = `Check out this job: ${job?.title} at ${job?.company?.name}`;
    
    const shareUrls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(shareUrl)}`,
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + ' ' + shareUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`
    };

    if (shareUrls?.[platform]) {
      window.open(shareUrls?.[platform], '_blank', 'width=600,height=400');
    }
    setShowShareMenu(false);
  };

  const formatSalary = (min, max) => {
    const formatNumber = (num) => {
      if (num >= 10000000) return `${(num / 10000000)?.toFixed(1)}Cr`;
      if (num >= 100000) return `${(num / 100000)?.toFixed(1)}L`;
      return num?.toLocaleString('en-IN');
    };
    return `₹${formatNumber(min)} - ₹${formatNumber(max)} per annum`;
  };

  const getDaysRemaining = (deadline) => {
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysRemaining = getDaysRemaining(job?.applicationDeadline);

  return (
    <div className="bg-card border-b border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">
          <div className="flex flex-col sm:flex-row gap-4 lg:gap-6 flex-1">
            <div className="flex-shrink-0">
              <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-lg overflow-hidden bg-muted border border-border">
                <Image
                  src={job?.company?.logo}
                  alt={job?.company?.logoAlt}
                  className="w-full h-full object-contain p-2"
                />
              </div>
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-start gap-3 mb-3">
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-foreground leading-tight">
                  {job?.title}
                </h1>
                {job?.isFeatured && (
                  <span className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 bg-accent/10 text-accent text-xs font-medium rounded">
                    <Icon name="Star" size={12} />
                    Featured
                  </span>
                )}
              </div>

              <button
                onClick={() => navigate('/company-profile')}
                className="inline-flex items-center gap-2 text-lg sm:text-xl font-semibold text-primary hover:underline mb-4 transition-smooth"
              >
                {job?.company?.name}
                <Icon name="ExternalLink" size={16} />
              </button>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-4">
                <div className="flex items-center gap-2 text-foreground">
                  <Icon name="Briefcase" size={18} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-sm sm:text-base">{job?.experience}</span>
                </div>

                <div className="flex items-center gap-2 text-foreground">
                  <Icon name="MapPin" size={18} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-sm sm:text-base">{job?.location}</span>
                </div>

                <div className="flex items-center gap-2 text-foreground">
                  <Icon name="Clock" size={18} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-sm sm:text-base">{job?.employmentType}</span>
                </div>

                <div className="flex items-center gap-2 text-foreground">
                  <Icon name="IndianRupee" size={18} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-sm sm:text-base font-medium">{formatSalary(job?.salary?.min, job?.salary?.max)}</span>
                </div>

                <div className="flex items-center gap-2 text-foreground">
                  <Icon name="Users" size={18} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-sm sm:text-base">{job?.openings} {job?.openings === 1 ? 'Opening' : 'Openings'}</span>
                </div>

                <div className="flex items-center gap-2 text-foreground">
                  <Icon name="Calendar" size={18} className="text-muted-foreground flex-shrink-0" />
                  <span className="text-sm sm:text-base">
                    {daysRemaining > 0 ? `${daysRemaining} days left` : 'Deadline passed'}
                  </span>
                </div>
              </div>

              {job?.workArrangement && (
                <div className="flex items-center gap-2 mt-4 px-3 py-2 bg-muted rounded-lg inline-flex">
                  <Icon name="Laptop" size={16} className="text-primary" />
                  <span className="text-sm font-medium text-foreground">{job?.workArrangement}</span>
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 lg:min-w-[200px]">
            <Button
              variant="default"
              size="lg"
              fullWidth
              iconName="Send"
              iconPosition="right"
              onClick={onApply}
              disabled={daysRemaining <= 0}
            >
              Apply Now
            </Button>

            <Button
              variant="outline"
              size="lg"
              fullWidth
              iconName={isSaved ? "Heart" : "Heart"}
              iconPosition="left"
              onClick={onSaveToWishlist}
              className={isSaved ? "text-error border-error" : ""}
            >
              {isSaved ? 'Saved' : 'Save Job'}
            </Button>

            <div className="relative">
              <Button
                variant="secondary"
                size="lg"
                fullWidth
                iconName="Share2"
                iconPosition="left"
                onClick={() => setShowShareMenu(!showShareMenu)}
              >
                Share
              </Button>

              {showShareMenu && (
                <>
                  <div 
                    className="fixed inset-0 z-40" 
                    onClick={() => setShowShareMenu(false)}
                  />
                  <div className="absolute top-full right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-modal z-50">
                    <div className="py-2">
                      <button
                        onClick={() => handleShare('linkedin')}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                      >
                        <Icon name="Linkedin" size={18} />
                        <span>LinkedIn</span>
                      </button>
                      <button
                        onClick={() => handleShare('twitter')}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                      >
                        <Icon name="Twitter" size={18} />
                        <span>Twitter</span>
                      </button>
                      <button
                        onClick={() => handleShare('whatsapp')}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                      >
                        <Icon name="MessageCircle" size={18} />
                        <span>WhatsApp</span>
                      </button>
                      <button
                        onClick={() => handleShare('facebook')}
                        className="w-full flex items-center gap-3 px-4 py-2 text-sm text-popover-foreground hover:bg-muted transition-smooth"
                      >
                        <Icon name="Facebook" size={18} />
                        <span>Facebook</span>
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {daysRemaining > 0 && daysRemaining <= 7 && (
          <div className="mt-6 flex items-center gap-2 px-4 py-3 bg-warning/10 border border-warning/20 rounded-lg">
            <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0" />
            <p className="text-sm text-warning font-medium">
              Application deadline is approaching! Only {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} left to apply.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default JobHeader;